/**
 * Department List Component
 *
 * Displays departments in a refined table with edit/delete actions.
 * Features polished styling with Lucide icons and hover states.
 */

import { Users, Pencil, Trash2 } from 'lucide-react';
import type { DepartmentWithCount } from '@guild/shared';

interface DepartmentListProps {
  departments: DepartmentWithCount[];
  isAdmin: boolean;
  onEdit: (department: DepartmentWithCount) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

export function DepartmentList({
  departments,
  isAdmin,
  onEdit,
  onDelete,
  isDeleting,
}: DepartmentListProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Department
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Operators
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            {isAdmin && (
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {departments.map((department) => (
            <tr
              key={department.id}
              className={`
                group transition-colors
                ${department.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
              `}
            >
              {/* Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-lg
                      ${department.isActive ? 'bg-guild-mint/30' : 'bg-gray-100'}
                    `}
                  >
                    <span
                      className={`
                        text-sm font-bold
                        ${department.isActive ? 'text-guild-green' : 'text-gray-400'}
                      `}
                    >
                      {department.name[0]?.toUpperCase()}
                    </span>
                  </div>
                  <span
                    className={`
                      text-sm font-semibold
                      ${department.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {department.name}
                  </span>
                </div>
              </td>

              {/* Description */}
              <td className="px-6 py-4">
                <div
                  className={`
                    text-sm max-w-xs truncate
                    ${department.isActive ? 'text-gray-500' : 'text-gray-400'}
                  `}
                >
                  {department.description || (
                    <span className="text-gray-300 italic">No description</span>
                  )}
                </div>
              </td>

              {/* Operator Count */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Users
                    size={16}
                    className={department.isActive ? 'text-gray-400' : 'text-gray-300'}
                  />
                  <span
                    className={`
                      text-sm font-medium
                      ${department.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {department.operatorCount}
                  </span>
                </div>
              </td>

              {/* Status */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
                    ${department.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >
                  <span
                    className={`
                      h-1.5 w-1.5 rounded-full
                      ${department.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
                    `}
                  />
                  {department.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>

              {/* Actions */}
              {isAdmin && (
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(department)}
                      className="
                        inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                        text-sm font-medium text-gray-600
                        hover:bg-gray-100 hover:text-gray-900
                        transition-colors
                      "
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                    {department.isActive && (
                      <button
                        onClick={() => onDelete(department.id)}
                        disabled={isDeleting}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                          text-sm font-medium text-red-600
                          hover:bg-red-50 hover:text-red-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors
                        "
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
